import { Message } from './types';
import { query, transaction } from './db';
import { sendEmail } from './email';

// Get all messages
export const getMessages = async (): Promise<Message[]> => {
  try {
    const messages = await query<Message[]>(
      'SELECT * FROM messages ORDER BY createdAt DESC'
    );
    return messages;
  } catch (error) {
    console.error('Error fetching messages:', error);
    return [];
  }
};

// Get unread messages count
export const getUnreadCount = async (): Promise<number> => {
  try {
    const result = await query<[{count: number}]>(
      'SELECT COUNT(*) as count FROM messages WHERE readAt IS NULL AND isArchived = false'
    );
    return result[0].count;
  } catch (error) {
    console.error('Error getting unread count:', error);
    return 0;
  }
};

// Save new message
export const saveMessage = async (message: Omit<Message, 'id' | 'createdAt'>): Promise<Message> => {
  try {
    const result = await query<{insertId: number}>(
      'INSERT INTO messages (senderName, senderEmail, content, createdAt, isArchived) VALUES (?, ?, ?, NOW(), false)',
      [message.senderName, message.senderEmail, message.content]
    );
    
    const newMessage: Message = {
      id: result.insertId.toString(),
      ...message,
      createdAt: new Date().toISOString(),
      isArchived: false
    };

    // Send email notification
    await sendNotificationEmail(newMessage);

    return newMessage;
  } catch (error) {
    console.error('Error saving message:', error);
    throw error;
  }
};

// Mark message as read
export const markAsRead = async (messageId: string): Promise<void> => {
  try {
    await query(
      'UPDATE messages SET readAt = NOW() WHERE id = ?',
      [messageId]
    );
  } catch (error) {
    console.error('Error marking message as read:', error);
    throw error;
  }
};

// Save reply
export const saveReply = async (messageId: string, reply: string): Promise<void> => {
  try {
    await query(
      'UPDATE messages SET reply = ?, repliedAt = NOW() WHERE id = ?',
      [reply, messageId]
    );

    // Get message details for email
    const messages = await query<Message[]>(
      'SELECT * FROM messages WHERE id = ?', 
      [messageId]
    );
    
    if (messages.length > 0) {
      // Send reply email
      await sendReplyEmail(messages[0], reply);
    }
  } catch (error) {
    console.error('Error saving reply:', error);
    throw error;
  }
};

// Archive message
export const archiveMessage = async (messageId: string): Promise<void> => {
  try {
    await query(
      'UPDATE messages SET isArchived = true WHERE id = ?',
      [messageId]
    );
  } catch (error) {
    console.error('Error archiving message:', error);
    throw error;
  }
};

// Delete message (GDPR compliance)
export const deleteMessage = async (messageId: string): Promise<void> => {
  try {
    await query('DELETE FROM messages WHERE id = ?', [messageId]);
  } catch (error) {
    console.error('Error deleting message:', error);
    throw error;
  }
};

// Send notification email to admin
const sendNotificationEmail = async (message: Message): Promise<void> => {
  const html = `
    <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
      <h1 style="color: #4F46E5;">Nouveau message sur PimpMyJob</h1>
      <p><strong>De :</strong> ${message.senderName} (${message.senderEmail})</p>
      <p><strong>Message :</strong></p>
      <div style="background-color: #F3F4F6; padding: 20px; border-radius: 8px; margin: 20px 0;">
        ${message.content}
      </div>
      <a href="${import.meta.env.VITE_APP_URL}/inbox" 
         style="display: inline-block; background-color: #4F46E5; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px;">
        Répondre au message
      </a>
    </div>
  `;

  try {
    await sendEmail(
      import.meta.env.VITE_SMTP_USER,
      'Nouveau message client',
      html
    );
  } catch (error) {
    console.error('Error sending notification email:', error);
  }
};

// Send reply email to client
const sendReplyEmail = async (message: Message, reply: string): Promise<void> => {
  const html = `
    <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
      <h1 style="color: #4F46E5;">Réponse à votre message</h1>
      <p>Bonjour ${message.senderName},</p>
      <p>Nous avons répondu à votre message :</p>
      <div style="background-color: #F3F4F6; padding: 20px; border-radius: 8px; margin: 20px 0;">
        <p><strong>Votre message :</strong></p>
        <p style="color: #666;">${message.content}</p>
        <hr style="border: none; border-top: 1px solid #ddd; margin: 20px 0;">
        <p><strong>Notre réponse :</strong></p>
        <p>${reply}</p>
      </div>
      <p style="color: #666;">
        Merci de nous avoir contacté !<br>
        L'équipe PimpMyJob
      </p>
    </div>
  `;

  try {
    await sendEmail(
      message.senderEmail,
      'Réponse à votre message - PimpMyJob',
      html
    );
  } catch (error) {
    console.error('Error sending reply email:', error);
  }
};